import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { get, post } from '@/features/shared/lib/api';
import { arr } from '@/features/shared/lib/utils';
import PageBreadcrumb from '@/features/shared/components/PageBreadcrumb';
import ResearcherTabs from '@/features/researcher/components/ResearcherTabs';
import ResearcherIntro from '@/features/researcher/components/ResearcherIntro';
import ResearcherMetrics from '@/features/researcher/components/ResearcherMetrics';
import ResearcherCoResearchers from '@/features/researcher/components/ResearcherCoResearchers';
import ResearcherResources from '@/features/researcher/components/ResearcherResources';
import ContactButtons from '@/features/researcher/components/ContactButtons';
import ResearcherSidebar from '@/features/researcher/components/ResearcherSidebar';

const TAB_KEYS = ['intro', 'metrics', 'co', 'pub'];
const tabToIndex = (tab) => (TAB_KEYS.includes(tab) ? TAB_KEYS.indexOf(tab) : 1);
const indexToTab = (i) => TAB_KEYS[i] ?? 'metrics';

export default function Researcher() {
  const { id: pid } = useParams();
  const [sp, setSearchParams] = useSearchParams();
  const location = useLocation();
  const id = pid || sp.get('id');
  const navigate = useNavigate();

  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [activeTab, setActiveTab] = useState(() => tabToIndex(sp.get('tab')));
  const [viewKey, setViewKey] = useState(0);

  // 즐겨찾기 상태 관리
  const [favorId, setFavorId] = useState(null);

  // Reset tab and remount tab contents when researcher id changes (tab은 URL에서 복원)
  useEffect(() => {
    if (!id) return;
    const tabFromUrl = tabToIndex(sp.get('tab'));
    setActiveTab(tabFromUrl);
    setViewKey((k) => k + 1);
    setTabCache({
      metrics: { data: null, loaded: false, error: null },
      co: { internal: [], external: [], loaded: false, error: null },
    });
    try { removeAltmetricScript(); } catch (e) {}
  }, [id]);

  // URL의 tab 파라미터 변경 시(뒤로가기/직접 입력 등) 탭 동기화
  useEffect(() => {
    const tabParam = sp.get('tab');
    if (tabParam && TAB_KEYS.includes(tabParam)) {
      setActiveTab(TAB_KEYS.indexOf(tabParam));
    }
  }, [sp]);

  // Per-tab cache to avoid re-fetch on tab switches (per scholar)
  const initialTabCache = useMemo(() => ({
    metrics: { data: null, loaded: false, error: null },
    co: { internal: [], external: [], loaded: false, error: null },
  }), []);
  const [tabCache, setTabCache] = useState(initialTabCache);

  useEffect(() => {
    if (!id) return;
    let alive = true;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const data = await get(`/rm/v1/scholar/${encodeURIComponent(id)}`);
        if (!alive) return;
        const rec = data?.result?.record || data?.result || null;
        setRecord(rec);
        // favor_id 초기화
        if (rec?.favor_id) {
          setFavorId(rec.favor_id);
        } else {
          setFavorId(null);
        }
      } catch (e) {
        if (!alive) return;
        setErr(e?.response?.data?.message || e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id]);

  useEffect(() => {
    if (!record) return;
    const nameKor = record.scholar_name_kor || record.scholar_name || '';
    const nameEng = record.scholar_name_eng || '';
    const displayName = [nameEng, nameKor ? `(${nameKor})` : ''].filter(Boolean).join('');
    const label = displayName || record.scholar_name || '';
    if (!label) return;
    const site =
      typeof rmVars !== 'undefined' && rmVars.siteName
        ? String(rmVars.siteName).trim()
        : '';
    document.title = site ? `${label} - Researcher - ${site}` : label;
  }, [record]);

  // 선호 연구자 추가/삭제 핸들러
  const handleFavoriteClick = async (e) => {
    e.stopPropagation();

    // rmVars.isLogin 체크
    const isLoggedIn = typeof rmVars !== 'undefined' && rmVars?.isLogin == true;

    if (!isLoggedIn) {
      alert('로그인후 이용해주세요.');
      return;
    }

    const currentUserId = typeof rmVars !== 'undefined' && rmVars?.scholar_id ? rmVars.scholar_id : null;
    if (!currentUserId || !id) return;

    try {
      if (favorId) {
        // 삭제
        const response = await post(`/rm/v1/scholar_favorite/${currentUserId}/delete`, {}, {
          params: { favor_id: favorId }
        });

        if (response?.status && response?.result?.success === true) {
          setFavorId(null);
          if (record) record.favor_id = null;
        }
      } else {
        // 추가
        const response = await post(`/rm/v1/scholar_favorite/${currentUserId}/update`, {}, {
          params: { favor_scholar_id: id }
        });

        if (response?.status && response?.result?.success === true && response?.result?.favor_id) {
          setFavorId(response.result.favor_id);
          if (record) record.favor_id = response.result.favor_id;
        }
      }
    } catch (error) {
      console.error('선호 연구자 처리 중 오류:', error);
      alert('처리 중 오류가 발생했습니다.');
    }
  };

  // Altmetric 스크립트 삭제 유틸
  const removeAltmetricScript = () => {
    // 스크립트 태그 삭제
    const existingScript = document.querySelector('script[src*="d1bxh8uas1mnw7.cloudfront.net/assets/embed.js"]');
    if (existingScript && existingScript.parentNode) {
      existingScript.parentNode.removeChild(existingScript);
    }
    // window.altmetric 초기화
    if (typeof window !== 'undefined' && window.altmetric) {
      try {
        delete window.altmetric;
      } catch (e) {
        // ignore
      }
    }
    // altmetric-embed-js 태그도 삭제 (embed.js가 생성한 태그)
    const embedScript = document.getElementById('altmetric-embed-js');
    if (embedScript && embedScript.parentNode) {
      embedScript.parentNode.removeChild(embedScript);
    }
  };

  // Altmetric 스크립트 로드 유틸
  const loadAltmetricScript = () => {
    return new Promise((resolve, reject) => {
      // 이미 로드되어 있는지 확인
      const existingScript = document.querySelector('script[src*="d1bxh8uas1mnw7.cloudfront.net/assets/embed.js"]');
      if (existingScript) {
        // 이미 로드되어 있고 window.altmetric가 준비되었는지 확인
        const checkAltmetric = (retries = 10) => {
          if (typeof window !== 'undefined' && window.altmetric && typeof window.altmetric.process === 'function') {
            resolve();
            return;
          }
          if (retries > 0) {
            setTimeout(() => checkAltmetric(retries - 1), 200);
          } else {
            resolve(); // 타임아웃되어도 resolve (재시도는 초기화 로직에서)
          }
        };
        checkAltmetric();
        return;
      }

      // 스크립트 동적 로드
      // 스크립트 동적 로드
      const script = document.createElement('script');
      script.src = 'https://d1bxh8uas1mnw7.cloudfront.net/assets/embed.js';
      script.async = true;
      document.body.appendChild(script);

      const script2 = document.createElement('script');
      script2.src = 'https://cdn.plu.mx/widget-popup.js';
      script2.async = true;
      document.body.appendChild(script2);
    });
  };

  // Resources 탭이 활성화되고 DOM 렌더링 완료 후 Altmetric 스크립트 로드 및 초기화
  useEffect(() => {
    // Resources 탭(activeTab === 3)이 아니면 스크립트 삭제 후 종료
    if (activeTab !== 3) {
      removeAltmetricScript();
      return;
    }

    // 데이터가 없거나 로딩 중이면 대기
    if (!record || loading) {
      return;
    }

    let isMounted = true;

    // 스크립트 로드 후 초기화
    loadAltmetricScript().then(() => {
      if (!isMounted) return;

      const initAltmetric = () => {
        // 스크립트와 window.altmetric 확인
        if (typeof window === 'undefined' || !window.altmetric || typeof window.altmetric.process !== 'function') {
          return false;
        }

        // DOM에 실제로 요소가 렌더링되었는지 확인 (ResearcherResources 컴포넌트 내부)
        // hidden 클래스가 제거되고 실제로 보이는 요소만 찾기
        const container = document.querySelector('.researcher-resources-container');
        if (!container || container.classList.contains('hidden')) {
          return false;
        }

        // 컨테이너가 실제로 보이는지 확인
        const isVisible = container.offsetParent !== null ||
          window.getComputedStyle(container).display !== 'none';
        if (!isVisible) {
          return false;
        }

        const elements = container.querySelectorAll('.altmetric-embed');
        if (elements.length === 0) {
          return false;
        }

        let processed = 0;
        elements.forEach((el) => {
          try {
            // 기존 처리 플래그 제거 (재처리를 위해)
            delete el.dataset.altmetricProcessed;
            window.altmetric.process(el);
            processed++;
          } catch (e) {
            console.error('[Researcher] Altmetric process error:', e);
          }
        });

        return processed > 0;
      };

      // requestAnimationFrame을 사용하여 DOM 렌더링 완료 후 초기화
      const tryInitWithDelay = () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!isMounted) return;
            // DOM 렌더링 완료 후 약간의 지연을 두고 초기화 (탭 전환 애니메이션 완료 대기)
            setTimeout(() => {
              if (!isMounted) return;
              if (!initAltmetric()) {
                // 첫 시도 실패시 재시도
                setTimeout(() => {
                  if (!isMounted) return;
                  if (!initAltmetric()) {
                    // 두 번째 시도 실패시 한 번 더 재시도
                    setTimeout(() => {
                      if (!isMounted) return;
                      if (!initAltmetric()) {
                        // 세 번째 시도 실패시 마지막 재시도
                        setTimeout(() => {
                          if (!isMounted) return;
                          initAltmetric();
                        }, 1000);
                      }
                    }, 800);
                  }
                }, 500);
              }
            }, 300);
          });
        });
      };

      tryInitWithDelay();
    }).catch((e) => {
      console.error('[Researcher] Failed to load Altmetric script:', e);
    });

    return () => {
      isMounted = false;
    };
  }, [activeTab, record, loading]);

  // id가 변경될 때 (다른 연구자 페이지로 이동 시) 스크립트 삭제
  useEffect(() => {
    return () => {
      removeAltmetricScript();
    };
  }, [id]);

  // 팝업에서 push 이동 후 뒤로가기 시 스크롤/탭 복원
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('researcher-back-restore');
      if (!raw) return;
      const saved = JSON.parse(raw);
      const currentPath = location.pathname + location.search;
      if (saved.path !== currentPath) return;
      sessionStorage.removeItem('researcher-back-restore');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (typeof window !== 'undefined' && typeof saved.scrollY === 'number') {
            window.scrollTo(0, saved.scrollY);
          }
        });
      });
    } catch (e) {}
  }, [location.pathname, location.search]);

  const handleTabChange = (idx) => {
    setActiveTab(idx);
    const tabKey = indexToTab(idx);
    navigate(`/researchers/${id}?tab=${tabKey}`, { replace: true });
  };

  if (!id) return <div className="p-6">잘못된 접근입니다.</div>;
  if (err) return <div className="p-6 text-red-600">에러: {String(err)}</div>;
  if (loading && !record) return <div className="p-6">Loading...</div>;

  const nameKor = record?.scholar_name_kor || record?.scholar_name || '';
  const nameEng = record?.scholar_name_eng || '';
  const displayName = [nameEng, nameKor ? `(${nameKor})` : ''].filter(Boolean).join('');
  const deptName = record?.dept_name || '';
  const colName = record?.col_name || '';
  const position = record?.sinbun_name || '';
  const email = record?.email || '';
  const homepage = record?.homepage || '';
  const tel = record?.tel || '';
  const lab = record?.lab_name || '';
  const researchArea = (record?.research_field && String(record.research_field).trim())
    || (Array.isArray(record?.research_keyword) ? record?.research_keyword.map(k => k?.keyword).filter(Boolean).join(' ; ') : '');

  const ids = Array.isArray(record?.research_ids) ? record.research_ids : [];
  const findId = (t) => {
    try {
      const it = ids.find(r => r?.id_type === t);
      return it?.id_value || '';
    } catch (e) {
      return '';
    }
  };
  const orcid = record?.orcid || findId('orcid');
  const isni = record?.isni || findId('isni');
  const scopusId = record?.scopus_id || findId('scopus_id');
  // const kciId = record?.kci_id || findId('kci_id');
  const googleId = record?.googlescholar_id || findId('googlescholar_id') || findId('google_scholar_id');
  const researchgateId = record?.researchgate_id || findId('researchgate_id');

  const researcherId = record?.researcher_id || findId('researcher_id');

  // Sidebar와 동일: H-Index, Scholarly Output, Citation Count (모바일용)
  const meta = record?.meta || {};
  const num = (v) => (typeof v === 'number' && !Number.isNaN(v) ? v : Number(v) || 0);
  const fmt = (v) => num(v).toLocaleString();
  const computedHIndex = {
    wos: meta.hindex_wos ?? 0,
    scopus: meta.hindex_scopus ?? 0,
    kci: meta.hindex_kci ?? 0,
  };
  const hIndexWosFmt = fmt(computedHIndex.wos);
  const hIndexScopusFmt = fmt(computedHIndex.scopus);
  const hIndexKciFmt = fmt(computedHIndex.kci);
  const raa = record?.research_all_achievement || {};
  const scholarlyOutputFmt = fmt(raa.resource_count ?? 0);
  const citationCountFmt = fmt(raa.cited_count ?? 0);

  const orcidUrl = orcid ? `https://orcid.org/${orcid}` : '';
  const isniUrl = isni ? `https://nl.go.kr/isni/${isni}` : '';
  const scopusUrl = scopusId ? `https://www.scopus.com/authid/detail.uri?authorId=${encodeURIComponent(scopusId)}` : '';
  const googleUrl = googleId ? `https://scholar.google.com/citations?user=${encodeURIComponent(googleId)}` : '';
  const researchgateUrl = researchgateId ? `https://www.researchgate.net/profile/${encodeURIComponent(researchgateId)}` : '';
  const wosUrl = researcherId ? `https://www.webofscience.com/wos/author/record/${encodeURIComponent(researcherId)}` : '';

  const profileSrc = (record?.profile_pic && String(record.profile_pic).trim() !== '')
    ? record.profile_pic
    : rmVars.themeUrl + 'images/profile.png';

  const onBackClick = (e) => {
    try {
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
    } catch { }
    // Prefer browser back to leverage list page POP hydration
    try {
      navigate(-1);
    } catch {
      // Fallback to list route if history is empty
      navigate('/researchers');
    }
  };

  return (
    <div className="researchdetailPage detailPage flexPage !gap-[40px] mt-[127px] lg:mt-[120px] lg:px-6 xl:px-[100px]">
      <div className="research-detail-cont w-full px-6 pt-[24px] lg:relative lg:w-[calc((100%-40px)*0.7)] lg:mt-[105px] lg:bg-white lg:rounded-xl lg:px-[60px] lg:py-[60px] lg:pt-[60px]">

        <button
          type="button"
          onClick={onBackClick}
          className="inline-flex items-center gap-[6px] bg-[#F3F5F9] mb-6 py-[10px] pl-[10px] pr-[16px] rounded-md lg:pl-0 lg:pr-0 lg:py-0 lg:bg-transparent lg:absolute lg:-top-[44px] lg:left-0"
        >
          <img src={rmVars.themeUrl + 'images/chevron_right_8D9AAF.png'} className="w-3 rotate-180" alt="back" />
          <span className="text-[14px] text-[#6C7687] font-medium leading-[100%] lg:text-[16px] xxl:text-red-500">
            Back<span className="hidden lg:inline"> to Researcher list</span>
          </span>
        </button>

        <button className="btn-print hidden px-3 py-[6px] items-center gap-[6px] bg-white rounded-[8px] cursor-pointer absolute lg:-top-[54px] lg:right-0 lg:flex">
          <img src={rmVars.themeUrl + 'images/print-ico.png'} className="w-[24px] h-[24px]" alt="filter" />
          <span className="text-[14px] text-[#6C7687] font-semibold leading-[100%]">Print</span>
        </button>

        <PageBreadcrumb
          current={displayName || (record?.scholar_name || '')}
          parent="Researcher"
          parentTo="/researchers"
          hideOnMobile
        />

        <div>
          <div className="relative xl:flex xl:items-start xl:gap-[28px] xl:flex-wrap">
            <div className="shrink-0 w-[68px] h-[68px] mb-[10px] rounded-full overflow-hidden border border-[#E4E8F1] xl:w-[96px] xl:h-[96px]">
              <img src={profileSrc} alt="photo" className="w-full h-full object-cover object-center" />
            </div>

            <div className="xl:w-[calc(100%-124px)]">
              <h2 className="mb-[7px] text-[18px] lg:text-[22px] leading-[130%] font-black xl:mb-[10px] 2xl:pr-[310px] flex items-center">
                {displayName || (record?.scholar_name || '')}
                {typeof rmVars !== 'undefined' && rmVars?.isEditor && id && (
                  <a
                    href={`${rmVars.homeUrl}/wp-admin/admin.php?page=scholar&record_id=${id}`}
                    className="inline-flex items-center justify-center w-[18px] h-[18px] shrink-0 ml-2"
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Edit Researcher"
                  >
                    <span
                      className="editor-edit"
                      style={{
                        width: '18px',
                        height: '18px',
                        filter: 'invert(11%) sepia(70%) saturate(6625%) hue-rotate(348deg) brightness(88%) contrast(99%)',
                        backgroundSize: '18px auto',
                        display: 'inline-block',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center center',
                        backgroundImage: `url(${rmVars.themeUrl}images/edit-regular.svg)`
                      }}
                    />
                  </a>
                )}
              </h2>

              <div className="flex flex-wrap gap-2 text-[14px] text-[#555] font-semibold">
                <span>{deptName || colName || ''}</span>
                {position && <span>/</span>}
                {position && <span>{position}</span>}
              </div>

              <div className="absolute top-0 right-0 flex gap-2 items-center">
                <ContactButtons email={email} homepage={homepage} variant="desktop" />

                <div
                  className="intrest flex-shrink-0 w-[38px] h-[38px] cursor-pointer group"
                  onClick={handleFavoriteClick}
                >
                  <img src={rmVars.themeUrl + 'images/intrest-default.png'} className={`interest-default group-hover:hidden ${favorId ? 'hidden' : ''}`} alt="interest-default" />
                  <img src={rmVars.themeUrl + 'images/intrest-hover.png'} className={`interest-hover hidden group-hover:block ${favorId ? '!hidden' : ''}`} alt="interest-hover" />
                  <img src={rmVars.themeUrl + 'images/intrest-active.png'} className={`interest-active ${favorId ? 'block' : 'hidden'}`} alt="interest-active" />
                </div>
              </div>

              <div className="flex flex-wrap gap-x-2 gap-y-1 mt-[15px]">
                {googleUrl && (
                  <a href={googleUrl} target="_blank" rel="noreferrer" className="lg:flex lg:items-center lg:gap-[5px] lg:text-[12px] lg:text-[#555] lg:font-semibold lg:bg-[#F3F5F9] lg:rounded-full lg:h-[33px] lg:pl-[6px] lg:pr-3">
                    <img src={rmVars.themeUrl + 'images/googlescholar-page.png'} className="w-[30px] lg:w-[20px]" alt="Google Scholar" />
                    <span className="hidden lg:block">Google Scholar</span>
                  </a>
                )}

                {orcidUrl && (
                  <a href={orcidUrl} target="_blank" rel="noreferrer" className="lg:flex lg:items-center lg:gap-[5px] lg:text-[12px] lg:text-[#555] lg:font-semibold lg:bg-[#F3F5F9] lg:rounded-full lg:h-[33px] lg:pl-[6px] lg:pr-3">
                    <img src={rmVars.themeUrl + 'images/orcid-page.png'} className="w-[30px] lg:w-[20px]" alt="ORCID" />
                    <span className="hidden lg:block">ORCID</span>
                  </a>
                )}

                {isniUrl && (
                  <a href={isniUrl} target="_blank" rel="noreferrer" className="lg:flex lg:items-center lg:gap-[5px] lg:text-[12px] lg:text-[#555] lg:font-semibold lg:bg-[#F3F5F9] lg:rounded-full lg:h-[33px] lg:pl-[6px] lg:pr-3">
                    <img src={rmVars.themeUrl + 'images/isni-page.png'} className="w-[30px] lg:w-[20px]" alt="ISNI" />
                    <span className="hidden lg:block">ISNI</span>
                  </a>
                )}

                {record?.kci_id && (
                  <a href={`https://www.kci.go.kr/kciportal/po/citationindex/poCretDetail.kci?citationBean.cretId=${record.kci_id}`} target="_blank" rel="noreferrer" className="badge bg-gray link-id link-kci_id lg:flex lg:items-center lg:gap-[5px] lg:text-[12px] lg:text-[#555] lg:font-semibold lg:bg-[#F3F5F9] lg:rounded-full lg:h-[33px] lg:pl-[6px] lg:pr-3">
                    <img src={rmVars.themeUrl + 'images/kci-page.png'} className="w-[30px] lg:w-[20px]" alt="KCI" />
                    <span className="hidden lg:block">KCI</span>
                  </a>
                )}

                {researchgateUrl && (
                  <a href={researchgateUrl} target="_blank" rel="noreferrer" className="lg:flex lg:items-center lg:gap-[5px] lg:text-[12px] lg:text-[#555] lg:font-semibold lg:bg-[#F3F5F9] lg:rounded-full lg:h-[33px] lg:pl-[6px] lg:pr-3">
                    <img src={rmVars.themeUrl + 'images/researchgate-page.png'} className="w-[30px] lg:w-[20px]" alt="ResearchGate" />
                    <span className="hidden lg:block">ResearchGate</span>
                  </a>
                )}

                {scopusUrl && (
                  <a href={scopusUrl} target="_blank" rel="noreferrer" className="lg:flex lg:items-center lg:gap-[5px] lg:text-[12px] lg:text-[#555] lg:font-semibold lg:bg-[#F3F5F9] lg:rounded-full lg:h-[33px] lg:pl-[6px] lg:pr-3">
                    <img src={rmVars.themeUrl + 'images/scopus-page.png'} className="w-[30px] lg:w-[20px]" alt="Scopus" />
                    <span className="hidden lg:block">Scopus</span>
                  </a>
                )}

                {wosUrl && (
                  <a href={wosUrl} target="_blank" rel="noreferrer" className="lg:flex lg:items-center lg:gap-[5px] lg:text-[12px] lg:text-[#555] lg:font-semibold lg:bg-[#F3F5F9] lg:rounded-full lg:h-[33px] lg:pl-[6px] lg:pr-3">
                    <img src={rmVars.themeUrl + 'images/wos-page.png'} className="w-[30px] lg:w-[20px]" alt="WoS" />
                    <span className="hidden lg:block">WoS</span>
                  </a>
                )}
              </div>

              <div className="flex flex-col gap-y-[14px] mt-6 pt-6 border-t border-t-[#E4E8F1] text-[14px] font-semibold leading-[100%] xl:border-b xl:border-b-[#E4E8F1] xl:flex-row xl:items-center xl:gap-x-3 xl:py-5 xl:mt-[18px]">

                {email && (
                  <>
                    <div className="flex gap-x-[10px] xl:items-center">
                      <div className="shrink-0 w-[76px] text-[#8D9AAF] font-medium xl:w-auto">
                        <img src={rmVars.themeUrl + 'images/email-ico.png'} className="hidden xl:block w-[18px]" alt="email" />
                        <span className="xl:hidden">Email</span>
                      </div>
                      <a href={`mailto:${email}`} className="xl:text-[#555]">{email}</a>
                    </div>
                    {(tel || lab) && (
                      <span className="hidden xl:block w-[1px] h-[17px] bg-[#E6E8EC]"></span>
                    )}
                  </>
                )}

                {tel && (
                  <>
                    <div className="flex gap-x-[10px] xl:items-center">
                      <div className="shrink-0 w-[76px] text-[#8D9AAF] font-medium xl:w-auto">
                        <img src={rmVars.themeUrl + 'images/phone-ico.png'} className="hidden xl:block w-[18px]" alt="tel" />
                        <span className="xl:hidden">Tel</span>
                      </div>
                      <div className="xl:text-[#555]">
                        {tel && /^[\d-]{7,20}$/.test(tel.trim()) ? (
                          <a href={`tel:${tel.trim()}`} className="hover:underline">
                            {tel}
                          </a>
                        ) : (
                          tel
                        )}
                      </div>
                    </div>
                    {lab && (
                      <span className="hidden xl:block w-[1px] h-[17px] bg-[#E6E8EC]"></span>
                    )}
                  </>
                )}

                {lab && (
                  <>
                    <div className="flex gap-x-[10px] xl:items-center">
                      <div className="shrink-0 w-[76px] text-[#8D9AAF] font-medium xl:w-auto">
                        <img src={rmVars.themeUrl + 'images/science-ico.png'} className="hidden xl:block w-[18px]" alt="lab" />
                        <span className="xl:hidden">Lab</span>
                      </div>
                      <div className="flex items-center gap-2 xl:text-[#555]">
                        {lab}
                        {record?.lab_link && (
                          <a href={record.lab_link} target="_blank" rel="noreferrer" className="flex items-center justify-center w-[22px] lg:w-[26px] h-[22px] lg:h-[26px] border border-[#E4E8F1] rounded-[8px]">
                            <img src={rmVars.themeUrl + 'images/launch-ico-gr.png'} className="w-[14px] lg:w-[18px]" alt="link" />
                          </a>
                        )}
                      </div>
                    </div>
                  </>
                )}

              </div>

              <div className="flex gap-x-[10px] mt-[14px] text-[14px] font-semibold leading-[100%] xl:mt-5 xl:items-start">
                <div className="shrink-0 w-[66px] text-[#8D9AAF] font-medium xl:w-auto xl:flex items-center gap-1">
                  <img src={rmVars.themeUrl + 'images/researcharea-ico.png'} className="hidden xl:block w-[18px]" alt="Research Area" />
                  <span>Research Area</span>
                </div>

                <div className="xl:mt-[2px] whitespace-pre-line leading-[150%]">
                  {Array.isArray(record?.research_field)
                    ? record.research_field.map(k => k?.keyword).filter(Boolean).join('\n')
                    : (record?.research_field || '')}
                </div>
              </div>

              <div className="flex gap-x-[10px] mt-[14px] text-[14px] font-semibold leading-[100%] lg:hidden">
                <div className="shrink-0 w-[76px] mt-[10px] text-[#8D9AAF] font-medium">H-Index</div>
                <div className="flex flex-wrap gap-[6px]">
                  <div className="px-[10px] py-[7px] text-[13px] font-medium text-[#532EB3] border border-[#E4E8F1] rounded-[12px]">
                    Web of Science <span className="ml-1 py-[2px] px-[6px] bg-[#532EB3] text-white font-bold rounded-full">{hIndexWosFmt}</span>
                  </div>

                  <div className="px-[10px] py-[7px] text-[13px] font-medium text-[#F3652D] border border-[#E4E8F1] rounded-[12px]">
                    Scopus <span className="ml-1 py-[2px] px-[6px] bg-[#F3652D] text-white font-bold rounded-full">{hIndexScopusFmt}</span>
                  </div>

                  <div className="px-[10px] py-[7px] text-[13px] font-medium text-[#2D7ECF] border border-[#E4E8F1] rounded-[12px]">
                    KCI <span className="ml-1 py-[2px] px-[6px] bg-[#2D7ECF] text-white font-bold rounded-full">{hIndexKciFmt}</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-x-[10px] mt-[14px] text-[14px] font-semibold leading-[100%] lg:hidden">
                <div className="shrink-0 w-[66px] text-[#8D9AAF] font-medium">Scholarly Output</div>
                <div>{scholarlyOutputFmt}</div>
              </div>

              <div className="flex gap-x-[10px] mt-[14px] text-[14px] font-semibold leading-[100%] lg:hidden">
                <div className="shrink-0 w-[66px] text-[#8D9AAF] font-medium">Citation Count</div>
                <div>{citationCountFmt}</div>
              </div>

            </div>

            <div className="chartarea p-6 mt-5 border border-[#E4E8F1] rounded-[12px] lg:hidden"></div>

            <ContactButtons email={email} homepage={homepage} variant="mobile" />
          </div>

          <div>
            <div id="introTooltip" className="fixed hidden w-[140px] p-4 text-white text-[12px] leading-[100%] bg-[#4B4DFF] rounded-[12px] sm:w-[180px] md:w-[200px] lg:w-[234px]">
              <p className="mb-[6px] pr-4 font-semibold">아직 소개글이 입력되지 않았습니다.</p>
              <a href="#" className="font-medium underline">내 정보 수정하러 가기</a>
              <button id="tooltipClose" className="absolute top-4 right-4 cursor-pointer">
                <img src={rmVars.themeUrl + 'images/clear-icon-fff.png'} className="w-[10px]" alt="close" />
              </button>
              <div className="absolute bottom-[-10px] left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[8px] border-r-[8px] border-t-[10px] border-l-transparent border-r-transparent border-t-[#4B4DFF]"></div>
            </div>

            <ResearcherTabs key={`tabs-${viewKey}`} activeTab={activeTab} onChange={handleTabChange} />

            <div className="researcher-tabM-cont tabM-cont">
              <ResearcherIntro active={activeTab === 0} record={record} loading={loading} />
              <ResearcherMetrics active={activeTab === 1} record={record} scholarId={id} />
              <ResearcherCoResearchers active={activeTab === 2} record={record} />
              <ResearcherResources active={activeTab === 3} record={record} />
            </div>
          </div>
        </div>
      </div>

      <ResearcherSidebar record={record} />
    </div>
  );
}
